'use client';

import { ListingCard } from '@components/listing-card';
import { AnalyticsRef } from '@components/analytics-ref';
import { useFavoritedListingIds } from '@hooks';
import { routes } from '@routes';
import type { RegionDataEntries } from './serp-content';

interface SerpListingsGridProps {
  regionDataEntries: RegionDataEntries;
  regionDescription: string;
}

/**
 * Client component that renders the listings grid with favorites hydration.
 * On mount, fetches the user's favorited listing IDs and passes the correct
 * isFavorited value to each ListingCard.
 */
export function SerpListingsGrid({
  regionDataEntries,
  regionDescription,
}: SerpListingsGridProps) {
  const { isFavorited, setFavorited } = useFavoritedListingIds();

  return (
    <AnalyticsRef name="serp_search_results">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
        {regionDataEntries
          .filter((entry): entry is NonNullable<typeof entry> =>
            Boolean(entry?.slug && entry?.account?.slug && entry?.id)
          )
          .slice(0, 8)
          .map((listing, index) => (
            <ListingCard
              key={listing.slug}
              listing={listing}
              altTag={regionDescription}
              accountShowRoute={routes.accounts(listing.account?.slug || '')}
              avatarUrl={listing.account?.avatarUrl}
              displayStay={true}
              showRoute={routes.listings({
                slug: listing.slug,
                checkInDate: listing.priceDisplay?.checkInDate,
                checkOutDate: listing.priceDisplay?.checkOutDate,
              })}
              priority={index < 4}
              isFavorited={isFavorited(listing.id)}
              onFavoritedSuccess={(newFavoritedState) => {
                setFavorited(listing.id, newFavoritedState);
              }}
            />
          ))}
      </div>
    </AnalyticsRef>
  );
}
